import { useEffect, useRef, useCallback, type ReactNode, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getVisibleFocusables(container: HTMLElement): HTMLElement[] {
  const all = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  return Array.from(all).filter((el) => {
    if (el.tagName.toLowerCase() === 'div' && el.hasAttribute('tabindex')) {
      return false;
    }
    const styles = globalThis.getComputedStyle(el);
    return (
      styles.display !== 'none' &&
      styles.visibility !== 'hidden' &&
      styles.opacity !== '0'
    );
  });
}

export interface FocusTrapWrapperProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  triggerRef?: RefObject<HTMLElement>;
  className?: string;
}

export function FocusTrapWrapper({
  children,
  isOpen,
  onClose,
  triggerRef,
  className,
}: Readonly<FocusTrapWrapperProps>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const firstFocusable = useRef<HTMLElement | null>(null);
  const lastFocusable = useRef<HTMLElement | null>(null);

  const refreshFocusables = useCallback(() => {
    if (!wrapperRef.current) return;
    const focusables = getVisibleFocusables(wrapperRef.current);
    firstFocusable.current = focusables[0] ?? null;
    lastFocusable.current = focusables[focusables.length - 1] ?? null;
  }, []);

  useEffect(() => {
    if (!isOpen || !wrapperRef.current) return;

    refreshFocusables();

    const raf = requestAnimationFrame(() => {
      firstFocusable.current?.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(raf);
  }, [isOpen, refreshFocusables]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        refreshFocusables();

        if (!firstFocusable.current || !lastFocusable.current) return;

        if (e.shiftKey) {
          if (document.activeElement === firstFocusable.current) {
            e.preventDefault();
            lastFocusable.current.focus({ preventScroll: true });
          }
        } else if (document.activeElement === lastFocusable.current) {
          e.preventDefault();
          firstFocusable.current.focus({ preventScroll: true });
        }
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, onClose, refreshFocusables]);

  useEffect(() => {
    if (isOpen) return;
    triggerRef?.current?.focus({ preventScroll: true });
  }, [isOpen, triggerRef]);

  return (
    <div ref={wrapperRef} className={className} aria-modal="true">
      {children}
    </div>
  );
}

export default FocusTrapWrapper;
